import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

const SALE_BADGE_SVG = `<svg
  class="inline-block h-[28px] w-auto shrink-0"
  viewBox="0 0 272 40"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
  role="img"
  aria-label="Sale - use code SUMMER20"
>
  <path
    d="M265.5467 36.5867H18.8524C18.1057 36.5867 17.3857 36.24 16.9324 35.6533L5.8657 21.52C5.17237 20.64 5.17237 19.3867 5.8657 18.5067L16.9057 4.34666C17.359 3.76 18.079 3.41333 18.8257 3.41333H265.5467C266.9067 3.41333 268 4.50666 268 5.86666V34.1333C268 35.4933 266.9067 36.5867 265.5467 36.5867Z"
    fill="#56A511"
  />
  <text
    x="24"
    y="25.7"
    font-family="Arial, Helvetica, sans-serif"
    font-size="16"
    font-weight="700"
    fill="#F8F4F2"
    text-anchor="start"
    textLength="236"
    lengthAdjust="spacingAndGlyphs"
  >SALE - USE CODE SUMMER20</text>
</svg>`;

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    
    // Store discount settings from the block
    this.discountPercentage = parseFloat(this.dataset.discountPercentage) || 0;
    this.badgeText = this.dataset.badgeText || '';
    this.subscriptionBadgeText = this.dataset.subscriptionBadgeText || '';
    this.showOriginalCompareAtPrice = this.dataset.showOriginalCompareAtPrice === 'true';
    this.showSaleSvg = this.dataset.showSaleSvg === 'true';
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Check if HTML was provided (standard variant picker)
    if (event.detail.data.html) {
      const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
      const currentPrice = this.querySelector('[ref="priceContainer"]');

      if (!newPrice || !currentPrice) return;

      if (currentPrice.innerHTML !== newPrice.innerHTML) {
        currentPrice.replaceWith(newPrice);
      }
    } else if (event.detail.resource) {
      // Vue variant picker - update price directly from variant data
      this.updatePriceFromVariant(event.detail.resource);
    }
  };

  /**
   * Updates price display from variant data (for Vue variant picker)
   * @param {Object} variant - The variant object with price information
   */
  updatePriceFromVariant(variant) {
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!currentPrice) return;

    // Format price using Shopify money format
    const formatMoney = (cents) => {
      const format = window.Shopify?.currency?.format || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      return format.replace('{{amount}}', value);
    };

    // Apply discount if discount_percentage is greater than 0
    let displayPrice = variant.price;
    let displayCompareAtPrice = variant.compare_at_price;
    
    if (this.discountPercentage > 0) {
      // If show_original_compare_at_price is true and original compare_at_price exists, use it
      if (this.showOriginalCompareAtPrice && variant.compare_at_price > 0) {
        displayCompareAtPrice = variant.compare_at_price;
      } else {
        displayCompareAtPrice = variant.price;
      }
      const discountMultiplier = (100 - this.discountPercentage) / 100;
      displayPrice = Math.round(variant.price * discountMultiplier);
    }

    // Build new price HTML
    let priceHTML = '';
    
    // Check if there's a sale (compare_at_price > price)
    const hasCompareAtPrice = displayCompareAtPrice && displayCompareAtPrice > displayPrice;
     
    if (hasCompareAtPrice) {
      // Show sale price first, then compare at price
      priceHTML = `
        <span role="group">
          <span class="visually-hidden">Sale price: </span>
          <span class="price">${formatMoney(displayPrice)}</span>
        </span>
        <span role="group">
          <span class="visually-hidden">Original price, crossed out: </span>
          <span class="compare-at-price" aria-label="Original price, crossed out: ${formatMoney(displayCompareAtPrice)}">
            ${formatMoney(displayCompareAtPrice)}
          </span>
        </span>
      `;
    } else {
      // Show regular price only
      priceHTML = `
        <span class="price">${formatMoney(displayPrice)}</span>
      `;
    }

    // Add SALE badge svg if the product is tagged (matches server-rendered snippet)
    if (this.showSaleSvg) {
      priceHTML += SALE_BADGE_SVG;
    }

    // Add badge if badge text is provided. A Subscribe & Save variant uses the
    // subscription badge when one is set, otherwise it falls back to the normal
    // badge text.
    const isSubscriptionVariant = !!(variant.title && /subscri/i.test(variant.title));
    const subscriptionBadge = (this.subscriptionBadgeText || '').trim();
    const badgeText =
      isSubscriptionVariant && subscriptionBadge !== ''
        ? subscriptionBadge
        : this.badgeText;

    if (badgeText && badgeText.trim() !== '') {
      priceHTML += `
        <span class="!block whitespace-nowrap text-left font-semibold text-[12px] md:text-[14px]">
          <span class="border-2 border-[#56A511] block font-semibold md:text-[16px] px-2 py-[2px] text-[14px] text-[#56A511] text-left">
            ${badgeText}
          </span>
        </span>
      `;
    }

    currentPrice.innerHTML = priceHTML;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
